import {
	isStoreAssistant,
	isStoreHuman,
	isStoreMessage,
	type StoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { getStoreMessageId } from './getStoreMessageId';
import { isCreateSurface } from './isCreateSurface';
import { surfaceIdOf } from './surfaceIdOf';

const A2UI_ACTION_MIME_TYPE = 'application/json+a2ui';

/** A HUMAN row whose content is an A2UI surface interaction, not a typed prompt. */
const isHumanA2UIActionRow = (message: StoreHuman): boolean => {
	const content = message.content.content as unknown;
	return (
		typeof content === 'object' &&
		content !== null &&
		(content as { data_parts_mime_type?: unknown }).data_parts_mime_type === A2UI_ACTION_MIME_TYPE
	);
};

/**
 * Surfaces live A2UI messages may still address, as surfaceId -> anchor messageId. Mirrors the
 * backend fold's model (`A2UISurfaceFold.kt`): a typed human prompt deactivates everything, and
 * within a prompt-delimited span only the last two created surfaces stay addressable.
 */
export const findAddressableSurfaces = (messages: StoreChatItem[]): Map<string, string> => {
	let activeSurfaceId: string | undefined;
	let activeAnchorId: string | undefined;
	let roundCreatedSurfaceId: string | undefined;
	let roundCreatedAnchorId: string | undefined;

	for (const message of messages) {
		if (!isStoreMessage(message)) {
			continue;
		}
		if (isStoreHuman(message) && !isHumanA2UIActionRow(message)) {
			activeSurfaceId = undefined;
			activeAnchorId = undefined;
			roundCreatedSurfaceId = undefined;
			roundCreatedAnchorId = undefined;
			continue;
		}
		if (!isStoreAssistant(message)) {
			continue;
		}
		const messageId = getStoreMessageId(message);
		for (const a2uiMessage of message.agenticUiMessages ?? []) {
			if (!isCreateSurface(a2uiMessage)) {
				continue;
			}
			const surfaceId = surfaceIdOf(a2uiMessage);
			if (!surfaceId || surfaceId === roundCreatedSurfaceId || surfaceId === activeSurfaceId) {
				continue;
			}
			activeSurfaceId = roundCreatedSurfaceId;
			activeAnchorId = roundCreatedAnchorId;
			roundCreatedSurfaceId = surfaceId;
			roundCreatedAnchorId = messageId;
		}
	}

	const addressable = new Map<string, string>();
	if (activeSurfaceId !== undefined && activeAnchorId !== undefined) {
		addressable.set(activeSurfaceId, activeAnchorId);
	}
	if (roundCreatedSurfaceId !== undefined && roundCreatedAnchorId !== undefined) {
		addressable.set(roundCreatedSurfaceId, roundCreatedAnchorId);
	}
	return addressable;
};
